/**
 * Transformers for Doctor Recipe API
 * Functions to convert between frontend and backend data formats
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  Ingredient,
  CreateIngredientData,
  PreparationStep,
} from './types';
import { API_BASE_URL } from '../../../../config/api';

const toAbsoluteMediaUrl = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) {
    return `${API_BASE_URL.replace(/\/$/, '')}${url}`;
  }
  return url;
};

/**
 * Helper to safely parse JSON arrays returned as strings by the backend
 */
const parseJsonArray = (val: unknown): unknown[] => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim()) {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Transforms frontend ingredient format to backend format
 * Frontend uses: name, contraindications (camelCase)
 * Backend expects: ingredient_name, contra_indications (snake_case)
 */
export const transformIngredientToBackend = (
  ingredient: CreateIngredientData,
): {
  ingredient_id?: string | number;
  ingredient_name: string;
  quantity: string;
  unit: string;
  contra_indications?: string;
  qualification?: string;
  is_substitute?: boolean;
  skip_for_contraindication?: boolean;
  parent_recipe_ingredient_id?: string | number | null;
  parent_contraindication_id?: string | number | null;
  // Phase 4B medicine fields
  type?: 'ingredient' | 'medicine';
  apply_topically?: boolean;
  how_often?: string;
  timing?: string;
  meal_timing?: string;
  duration_value?: string;
  duration_unit?: string;
  can_use_indefinitely?: boolean;
  stop_after?: string;
  stop_after_unit?: string;
  size_unit?: string;
  dosage_instructions?: string;
} => {
  return {
    ingredient_id: ingredient.ingredient_id,
    ingredient_name: ingredient.name,
    quantity: ingredient.quantity,
    unit: ingredient.unit,
    contra_indications: ingredient.contraindications || undefined,
    qualification: ingredient.qualification || undefined,
    is_substitute: ingredient.is_substitute ?? undefined,
    skip_for_contraindication:
      ingredient.skip_for_contraindication ?? undefined,
    parent_recipe_ingredient_id:
      ingredient.parent_recipe_ingredient_id ?? undefined,
    parent_contraindication_id:
      ingredient.parent_contraindication_id ?? undefined,
    // Phase 4B — pass type so the backend knows if this is a medicine
    type: ingredient.type ?? 'ingredient',
    apply_topically: ingredient.apply_topically,
    how_often: ingredient.how_often || undefined,
    timing: ingredient.timing || undefined,
    meal_timing: ingredient.meal_timing || undefined,
    duration_value: ingredient.duration_value || undefined,
    duration_unit: ingredient.duration_unit || undefined,
    can_use_indefinitely: ingredient.can_use_indefinitely,
    stop_after: ingredient.stop_after || undefined,
    stop_after_unit: ingredient.stop_after_unit || undefined,
    size_unit: ingredient.size_unit || undefined,
    dosage_instructions: (ingredient as any).dosage_instructions || undefined,
  };
};

/**
 * Transforms backend ingredient format to frontend format
 * Backend returns: ingredient_name, contra_indications (snake_case)
 * Frontend uses: name, contraindications (camelCase)
 */
export const transformIngredientFromBackend = (ingredientData: {
  id?: string | number;
  ingredient_id?: string | number;
  ingredient_name?: string;
  name?: string;
  quantity?: string | number;
  unit?: string;
  contra_indications?: string;
  contraindications?: string;
  qualification?: string;
  is_substitute?: boolean;
  skip_for_contraindication?: boolean;
  parent_recipe_ingredient_id?: string | number | null;
  parent_ingredient_id?: string | number | null;
  parent_contraindication_id?: string | number | null;
  // Phase 4B medicine fields
  type?: 'ingredient' | 'medicine';
  apply_topically?: boolean;
  how_often?: string;
  timing?: string;
  meal_timing?: string;
  duration_value?: string;
  duration_unit?: string;
  can_use_indefinitely?: boolean;
  stop_after?: string;
  stop_after_unit?: string;
  size_unit?: string;
  dosage_instructions?: string;
  apply_instruction?: string;
  apply_for_value?: string;
  apply_for_uom?: string;
}): Ingredient => {
  const parentRecipeIngredientId =
    ingredientData.parent_recipe_ingredient_id ??
    ingredientData.parent_ingredient_id ??
    null;

  const qty = String(ingredientData.quantity || '').trim();
  const u = String(ingredientData.unit || '').trim();
  return {
    id: ingredientData.id,
    ingredient_id: ingredientData.ingredient_id,
    ingredient_name: ingredientData.ingredient_name,
    name: ingredientData.ingredient_name || ingredientData.name || '',
    quantity: qty,
    unit: u,
    contra_indications: ingredientData.contra_indications,
    qualification: ingredientData.qualification,
    contraindications:
      ingredientData.contra_indications ||
      ingredientData.contraindications ||
      undefined,
    is_substitute: ingredientData.is_substitute,
    skip_for_contraindication: ingredientData.skip_for_contraindication,
    parent_recipe_ingredient_id: parentRecipeIngredientId,
    parent_contraindication_id: ingredientData.parent_contraindication_id,
    // Phase 4B — medicine fields
    type: ingredientData.type ?? 'ingredient',
    apply_topically: ingredientData.apply_topically,
    how_often: ingredientData.how_often,
    timing: ingredientData.timing,
    meal_timing: ingredientData.meal_timing,
    duration_value: ingredientData.duration_value,
    duration_unit: ingredientData.duration_unit,
    can_use_indefinitely: ingredientData.can_use_indefinitely,
    stop_after: ingredientData.stop_after,
    stop_after_unit: ingredientData.stop_after_unit,
    size_unit: ingredientData.size_unit,
    dosage_instructions: ingredientData.dosage_instructions,
    apply_instruction: ingredientData.apply_instruction,
    apply_for_value: ingredientData.apply_for_value,
    apply_for_uom: ingredientData.apply_for_uom,
  };
};

/**
 * Transforms backend step format to frontend format
 * Backend returns: image, video (URL strings)
 * Frontend uses: videoUrl (camelCase to match component types)
 */
export const transformStepFromBackend = (stepData: {
  id?: string | number;
  step_number?: number;
  title?: string;
  description?: string;
  image?: string | null;
  image_url?: string | null;
  imageUrl?: string | null;
  step_image?: string | null;
  StepImage?: string | null;
  img?: string | null;
  video?: string | null;
  video_url?: string | null;
  videoUrl?: string | null;
}): PreparationStep => {
  // Extract image URL from various possible field names
  const imageUrl =
    stepData.image ||
    stepData.image_url ||
    stepData.imageUrl ||
    stepData.step_image ||
    stepData.StepImage ||
    stepData.img ||
    undefined;

  // Extract video URL from various possible field names
  const videoUrl =
    stepData.video || stepData.video_url || stepData.videoUrl || undefined;

  return {
    id: stepData.id,
    title: stepData.title || '',
    description: stepData.description || '',
    image_url: toAbsoluteMediaUrl(imageUrl),
    video_url: toAbsoluteMediaUrl(videoUrl),
    videoUrl: toAbsoluteMediaUrl(videoUrl), // Also set camelCase version for compatibility
    step_number: stepData.step_number,
  };
};

/**
 * Transforms backend recipe response to frontend format
 * Handles nested structures (ingredients, doshas, seasons, tags)
 */
export const transformRecipeFromBackend = (recipeData: {
  id?: string | number;
  author_id?: string | number;
  title?: string;
  description?: string;
  category_id?: string;
  prep_time_minutes?: number;
  servings?: number;
  warnings?: string;
  side_effects?: string;
  contraindications?: string;
  nutritional_info?: string;
  unit_id?: number | string;
  frequency_id?: number | string;
  timing_id?: number | string;
  meal_timing_id?: number | string;
  duration_days?: number | string;
  duration_unit_id?: number | string;
  max_limit_days?: number | string;
  max_duration_unit_id?: number | string;
  suitability_json?: unknown[];
  q_and_a_json?: unknown[];
  common_mistakes_json?: unknown[];
  ref_links?: unknown[];
  helps_with_diseases_other?: string;
  dosage?: {
    quantity?: string;
    unit?: string;
    frequency?: string;
    instructions?: string;
  } | null;
  dosage_qty?: string;
  dosage_instructions?: string;
  apply_for_value?: number | string | null;
  apply_for_uom?: string | null;
  medical_conditions_grid?: unknown[];
  natural_condition_ids?: unknown[];
  recommended_diet?: string;
  diet_to_avoid?: string;
  cover_image?: string | null;
  cover_gallery_images?: string[];
  cover_video?: string | null;
  preparation_video?: string | null;
  status?: string;
  edit_version?: number;
  createdAt?: string;
  updatedAt?: string;
  steps?: Array<{
    id?: string | number;
    step_number?: number;
    title?: string;
    description?: string;
    image?: string | null;
    video?: string | null;
  }>;
  approvals?: Array<{
    id?: string | number;
    decision?: 'approved' | 'rejected' | 'changes_requested' | null;
    comments?: string | null;
    reviewer?: {
      id?: string | number;
      name?: string;
      email?: string;
    };
    createdAt?: string;
    updatedAt?: string;
  }>;
  adminNotes?: Array<{
    decision?: 'approved' | 'rejected' | 'changes_requested' | null;
    comments?: string;
    reviewerName?: string;
    date?: string;
  }>;
  doshas_note?: string;
  doshas_ok_for_others?: boolean;
  seasons_note?: string;
  seasons_ok_for_others?: boolean;
  gender_note?: string;
  gender_ok_for_others?: boolean;
  age_group_note?: string;
  age_group_ok_for_others?: boolean;
  region_note?: string;
  region_ok_for_others?: boolean;
  maternity_note?: string;
  maternity_ok_for_others?: boolean;
  doshas?: Array<{
    id?: string | number;
    name?: string;
    RecipeDosha?: {
      recipe_id?: string;
      dosha_id?: string;
    };
  }>;
  ingredients?: Array<{
    id?: string | number;
    name?: string;
    RecipeIngredient?: {
      id?: string | number;
      quantity?: string | number;
      unit?: string;
      contra_indications?: string;
      contraindications?: string;
      qualification?: string;
      is_substitute?: boolean;
      skip_for_contraindication?: boolean;
      parent_recipe_ingredient_id?: string | number | null;
      parent_contraindication_id?: string | number | null;
      // Phase 4B
      type?: 'ingredient' | 'medicine';
      apply_topically?: boolean;
      how_often?: string;
      timing?: string;
      meal_timing?: string;
      duration_value?: string;
      duration_unit?: string;
      can_use_indefinitely?: boolean;
      stop_after?: string;
      stop_after_unit?: string;
      size_unit?: string;
    };
    recipe_ingredient?: {
      id?: string | number;
      quantity?: string | number;
      unit?: string;
      contra_indications?: string;
      contraindications?: string;
      qualification?: string;
      is_substitute?: boolean;
      skip_for_contraindication?: boolean;
      parent_recipe_ingredient_id?: string | number | null;
      parent_contraindication_id?: string | number | null;
      type?: 'ingredient' | 'medicine';
      apply_topically?: boolean;
      how_often?: string;
      timing?: string;
      meal_timing?: string;
      duration_value?: string;
      duration_unit?: string;
      can_use_indefinitely?: boolean;
      stop_after?: string;
      stop_after_unit?: string;
      size_unit?: string;
    };
    recipeIngredient?: {
      id?: string | number;
      quantity?: string | number;
      unit?: string;
      contra_indications?: string;
      contraindications?: string;
      qualification?: string;
      is_substitute?: boolean;
      skip_for_contraindication?: boolean;
      parent_recipe_ingredient_id?: string | number | null;
      parent_contraindication_id?: string | number | null;
      type?: 'ingredient' | 'medicine';
      apply_topically?: boolean;
      how_often?: string;
      timing?: string;
      meal_timing?: string;
      duration_value?: string;
      duration_unit?: string;
      can_use_indefinitely?: boolean;
      stop_after?: string;
      stop_after_unit?: string;
      size_unit?: string;
    };
  }>;
  seasons?: Array<
    | {
        id?: string | number;
        name?: string;
      }
    | string
  >;
  tags?: Array<
    | {
        id?: string | number;
        name?: string;
      }
    | string
  >;
  categories?: Array<{
    id?: string | number;
    name?: string;
  }>;
  regions?: Array<{ id?: string | number; name?: string }>;
  genders?: Array<{ id?: string | number; name?: string }>;
  ageGroups?: Array<{ id?: string | number; name?: string }>;
  symptoms?: Array<{ id?: string | number; name?: string }>;
  contraindicationList?: Array<{ id?: string | number; name?: string }>;
  natural_conditions?: Array<{ id?: string | number; name?: string }>;
  [key: string]: unknown;
}): {
  id?: string | number;
  author_id?: string | number;
  title: string;
  description?: string;
  category_id?: string;
  preparation_time?: number;
  servings?: number;
  warnings?: string;
  nutritional_info?: string;
  cover_image_url?: string;
  cover_gallery_images?: string[];
  cover_video_url?: string;
  preparation_video_url?: string;
  status?: string;
  edit_version?: number;
  visibility_scope?: 'personal' | 'team';
  on_behalf_of_team_id?: string | number | null;
  created_by_user_id?: string | number | null;
  createdAt?: string;
  updatedAt?: string;
  doshas?: string[];
  dosha_ids?: Array<string | number>;
  ingredients?: Array<{
    id?: string | number;
    name: string;
    quantity: string;
    unit: string;
    contraindications?: string;
  }>;
  seasons?: string[];
  tags?: string[];
  tag_ids?: Array<string | number>;
  category?: {
    id?: string | number;
    name?: string;
  };
  categories?: Array<{
    id?: string | number;
    name: string;
  }>;
  steps?: PreparationStep[];
  preparations?: any[];
  approvals?: Array<{
    id?: string | number;
    decision?: 'approved' | 'rejected' | 'changes_requested' | null;
    comments?: string | null;
    reviewer?: {
      id?: string | number;
      name?: string;
      email?: string;
    };
    createdAt?: string;
    updatedAt?: string;
  }>;
  adminNotes?: Array<{
    decision?: 'approved' | 'rejected' | 'changes_requested' | null;
    comments?: string;
    reviewerName?: string;
    date?: string;
  }>;
  // Additional fields used by the doctor wizard
  region_ids?: Array<string | number>;
  gender_ids?: Array<string | number>;
  age_group_ids?: Array<string | number>;
  helpsWithDiseases?: Array<string | number>;
  contraindications?: Array<string | number>;
  helpsWithDiseasesOther?: string;
  suitability_json?: unknown[];
  q_and_a_json?: unknown[];
  common_mistakes_json?: unknown[];
  ref_links?: unknown[];
  unit_id?: number;
  frequency_id?: number;
  timing_id?: number;
  meal_timing_id?: number;
  duration_days?: number;
  duration_unit_id?: number;
  max_limit_days?: number;
  max_duration_unit_id?: number;
  dosage_qty?: string;
  dosage_instructions?: string;
  is_indefinite?: boolean;
  apply_topically?: boolean;
  apply_for_value?: number;
  apply_for_uom?: string;
  how_many_days?: number;
  how_many_days_unit_id?: number;
  gap_days?: number;
  gap_days_unit_id?: number;
  doshas_note?: string;
  doshas_ok_for_others?: boolean;
  seasons_note?: string;
  seasons_ok_for_others?: boolean;
  gender_note?: string;
  gender_ok_for_others?: boolean;
  age_group_note?: string;
  age_group_ok_for_others?: boolean;
  region_note?: string;
  region_ok_for_others?: boolean;
  maternity_note?: string;
  maternity_ok_for_others?: boolean;
  contraindications_notes?: string;
  side_effects?: string;
  season_ids?: Array<string | number>;
  medical_conditions_grid?: Array<{
    condition_id: number | string;
    status: string;
  }>;
  symptoms_grid?: Array<{
    symptom_id: string | number;
    note?: string;
  }>;
  contraindications_grid?: Array<{
    contraindication_id: string | number;
    notes?: string;
  }>;
  natural_condition_ids?: Array<string | number>;
  recommended_diet?: string;
  diet_to_avoid?: string;
  general_exercise_notes?: string;
  custom_list_values?: Record<string, string[]>;
  exercises?: Array<{
    exercise_id?: string | number;
    note?: string;
    name?: string;
  }>;
} => {
  // Extract dosha names from nested structure
  const doshaNames =
    recipeData.doshas
      ?.map((dosha) => {
        if (typeof dosha === 'string') return dosha;
        return dosha.name || '';
      })
      .filter(Boolean) || [];

  // Extract dosha IDs
  const doshaIds =
    recipeData.doshas
      ?.map((dosha) => {
        if (typeof dosha === 'object' && dosha?.id !== undefined) {
          return typeof dosha.id === 'string' ? Number(dosha.id) : dosha.id;
        }
        return null;
      })
      .filter((id): id is number => id !== null) ||
    parseJsonArray((recipeData as any).dosha_ids)
      .map(Number)
      .filter((n) => !isNaN(n));

  // Extract gender IDs
  const genderIds =
    recipeData.genders
      ?.map((g: { id?: string | number }) =>
        g?.id !== undefined
          ? typeof g.id === 'string'
            ? Number(g.id)
            : g.id
          : null,
      )
      .filter((id): id is number => id !== null) ||
    parseJsonArray((recipeData as any).gender_ids)
      .map(Number)
      .filter((n) => !isNaN(n));

  // Extract age group IDs
  const ageGroupIds =
    recipeData.ageGroups
      ?.map((g: { id?: string | number }) =>
        g?.id !== undefined
          ? typeof g.id === 'string'
            ? Number(g.id)
            : g.id
          : null,
      )
      .filter((id): id is number => id !== null) ||
    parseJsonArray((recipeData as any).age_group_ids)
      .map(Number)
      .filter((n) => !isNaN(n));

  // Extract region IDs
  const regionIds =
    recipeData.regions
      ?.map((r: { id?: string | number }) =>
        r?.id !== undefined
          ? typeof r.id === 'string'
            ? Number(r.id)
            : r.id
          : null,
      )
      .filter((id): id is number => id !== null) ||
    parseJsonArray((recipeData as any).region_ids)
      .map(Number)
      .filter((n) => !isNaN(n));

  // Extract symptom IDs
  const symptomIds =
    recipeData.symptoms
      ?.map((s: { id?: string | number }) =>
        s?.id !== undefined
          ? typeof s.id === 'string'
            ? Number(s.id)
            : s.id
          : null,
      )
      .filter((id): id is number => id !== null) || [];

  const symptomsGrid = (() => {
    const raw = (recipeData as { symptoms_grid?: unknown }).symptoms_grid;
    const parsed = parseJsonArray(raw);
    const symptomNameById = new Map<string, string>();
    (recipeData.symptoms || []).forEach(
      (s: { id?: string | number; name?: string }) => {
        if (s?.id != null && s?.name) {
          symptomNameById.set(String(s.id), s.name);
        }
      },
    );
    if (parsed.length > 0) {
      return parsed
        .map((r: any) => {
          const row = r as {
            symptom_id?: string | number;
            name?: string | null;
            note?: string;
          };
          const symptom_id = row?.symptom_id ?? '';
          if (
            symptom_id === '' ||
            symptom_id === null ||
            symptom_id === undefined
          )
            return null;
          const name =
            (row?.name && String(row.name).trim()) ||
            symptomNameById.get(String(symptom_id)) ||
            null;
          return {
            symptom_id,
            name,
            note: row?.note ?? '',
          };
        })
        .filter(
          (
            r,
          ): r is {
            symptom_id: string | number;
            name: string | null;
            note: string;
          } => r !== null,
        );
    }
    // Do NOT synthesize grid rows from flat IDs — that would auto-fill the
    // Conditions tab even when the user never filled in this section.
    return [];
  })();

  const contraIds =
    recipeData.contraindicationList
      ?.map((c: { id?: string | number }) =>
        c?.id !== undefined
          ? typeof c.id === 'string'
            ? Number(c.id)
            : c.id
          : null,
      )
      .filter((id): id is number => id !== null) ||
    parseJsonArray(
      (recipeData as any).contra_indication_ids ||
        (recipeData as any).contraindications,
    )
      .map(Number)
      .filter((n) => !isNaN(n));

  const contraindicationsGrid = (() => {
    const raw = (recipeData as { contraindications_grid?: unknown })
      .contraindications_grid;
    const parsed = parseJsonArray(raw);
    if (parsed.length > 0) {
      return parsed
        .map((r: any) => {
          const row = r as {
            contraindication_id?: string | number;
            notes?: string;
          };
          const contraindication_id = row?.contraindication_id ?? '';
          if (
            contraindication_id === '' ||
            contraindication_id === null ||
            contraindication_id === undefined
          )
            return null;
          return {
            contraindication_id,
            notes: row?.notes ?? '',
          };
        })
        .filter(
          (r): r is { contraindication_id: string | number; notes: string } =>
            r !== null,
        );
    }
    // Do NOT synthesize grid rows from flat IDs — that would auto-fill the
    // Conditions tab even when the user never filled in this section.
    return [];
  })();

  // Extract natural condition IDs from natural_conditions association
  const naturalConditionIds =
    recipeData.natural_conditions
      ?.map((nc: { id?: string | number }) =>
        nc?.id !== undefined
          ? typeof nc.id === 'string'
            ? Number(nc.id)
            : nc.id
          : null,
      )
      .filter((id): id is number => id !== null) ||
    parseJsonArray((recipeData as any).natural_condition_ids)
      .map(Number)
      .filter((n) => !isNaN(n));

  // Extract categories
  const categories =
    recipeData.categories?.map((c) => ({
      id: c.id,
      name: c.name || '',
    })) || [];

  // Extract ingredients using existing transformer
  // Backend may return nested (RecipeIngredient) or flat format - handle both
  const transformedIngredients =
    recipeData.ingredients?.map((ing) => {
      const recipeIng =
        (ing as { RecipeIngredient?: Record<string, unknown> })
          .RecipeIngredient ||
        (ing as { recipe_ingredient?: Record<string, unknown> })
          .recipe_ingredient ||
        (ing as { recipeIngredient?: Record<string, unknown> })
          .recipeIngredient ||
        {};
      const flat = ing as {
        id?: string | number;
        ingredient_id?: string | number;
        name?: string;
        ingredient_name?: string;
        quantity?: string | number;
        unit?: string;
        contra_indications?: string;
        contraindications?: string;
        qualification?: string;
        is_substitute?: boolean;
        skip_for_contraindication?: boolean;
        parent_recipe_ingredient_id?: string | number | null;
        parent_contraindication_id?: string | number | null;
      };
      // Merge: use recipeIng when nested, fallback to flat when backend returns flat format
      const ri = recipeIng as Record<string, unknown>;
      const mergedIngredient = {
        id: (ri.id ?? flat.id) as string | number | undefined,
        ingredient_id: (flat.ingredient_id ??
          (ing as { id?: string | number }).id) as string | number | undefined,
        ingredient_name: (flat.ingredient_name ??
          flat.name ??
          (ing as { ingredient_name?: string }).ingredient_name) as
          | string
          | undefined,
        name: (flat.name ??
          flat.ingredient_name ??
          (ing as { name?: string }).name) as string | undefined,
        quantity: (ri.quantity ?? flat.quantity) as string | number | undefined,
        unit: (ri.unit ?? flat.unit) as string | undefined,
        contra_indications: (ri.contra_indications ??
          flat.contra_indications ??
          ri.contraindications ??
          flat.contraindications) as string | undefined,
        contraindications: (ri.contra_indications ??
          flat.contra_indications ??
          ri.contraindications ??
          flat.contraindications) as string | undefined,
        qualification: (ri.qualification ?? flat.qualification) as
          | string
          | undefined,
        is_substitute: (ri.is_substitute ?? flat.is_substitute) as
          | boolean
          | undefined,
        skip_for_contraindication: (ri.skip_for_contraindication ??
          flat.skip_for_contraindication) as boolean | undefined,
        parent_recipe_ingredient_id: (ri.parent_recipe_ingredient_id ??
          flat.parent_recipe_ingredient_id) as
          | string
          | number
          | null
          | undefined,
        parent_contraindication_id: (ri.parent_contraindication_id ??
          flat.parent_contraindication_id) as
          | string
          | number
          | null
          | undefined,
        // Phase 4B — medicine fields (prefer nested join-table, fallback to flat)
        type: (ri.type ??
          (flat as { type?: 'ingredient' | 'medicine' }).type ??
          'ingredient') as 'ingredient' | 'medicine',
        apply_topically: (ri.apply_topically ??
          (flat as { apply_topically?: boolean }).apply_topically) as
          | boolean
          | undefined,
        how_often: (ri.how_often ??
          (flat as { how_often?: string }).how_often) as string | undefined,
        timing: (ri.timing ?? (flat as { timing?: string }).timing) as
          | string
          | undefined,
        meal_timing: (ri.meal_timing ??
          (flat as { meal_timing?: string }).meal_timing) as string | undefined,
        duration_value: (ri.duration_value ??
          (flat as { duration_value?: string }).duration_value) as
          | string
          | undefined,
        duration_unit: (ri.duration_unit ??
          (flat as { duration_unit?: string }).duration_unit) as
          | string
          | undefined,
        can_use_indefinitely: (ri.can_use_indefinitely ??
          (flat as { can_use_indefinitely?: boolean }).can_use_indefinitely) as
          | boolean
          | undefined,
        stop_after: (ri.stop_after ??
          (flat as { stop_after?: string }).stop_after) as string | undefined,
        stop_after_unit: (ri.stop_after_unit ??
          (flat as { stop_after_unit?: string }).stop_after_unit) as
          | string
          | undefined,
        size_unit: (ri.size_unit ??
          (flat as { size_unit?: string }).size_unit) as string | undefined,
        dosage_instructions: (ri.dosage_instructions ??
          (flat as { dosage_instructions?: string }).dosage_instructions) as
          | string
          | undefined,
        apply_instruction: (ri.apply_instruction ??
          (flat as { apply_instruction?: string }).apply_instruction) as
          | string
          | undefined,
        apply_for_value: (ri.apply_for_value ??
          (flat as { apply_for_value?: string }).apply_for_value) as
          | string
          | undefined,
        apply_for_uom: (ri.apply_for_uom ??
          (flat as { apply_for_uom?: string }).apply_for_uom) as
          | string
          | undefined,
      };
      return transformIngredientFromBackend(mergedIngredient);
    }) || [];

  // Extract season names
  const seasonNames =
    recipeData.seasons
      ?.map((season) => {
        if (typeof season === 'string') return season;
        return season.name || '';
      })
      .filter(Boolean) || [];

  // Extract season IDs
  const seasonIds =
    recipeData.seasons
      ?.map((season) => {
        if (typeof season === 'object' && season?.id !== undefined) {
          return typeof season.id === 'string' ? Number(season.id) : season.id;
        }
        return null;
      })
      .filter((id): id is number => id !== null) ||
    parseJsonArray((recipeData as any).season_ids || (recipeData as any).season)
      .map(Number)
      .filter((n) => !isNaN(n));

  // Extract tag names
  const tagNames =
    recipeData.tags
      ?.map((tag) => {
        if (typeof tag === 'string') return tag;
        return tag.name || '';
      })
      .filter(Boolean) || [];

  // Extract tag IDs
  const tagIds =
    recipeData.tags
      ?.map((tag) => {
        if (typeof tag === 'object' && tag?.id !== undefined) {
          return typeof tag.id === 'string' ? Number(tag.id) : tag.id;
        }
        return null;
      })
      .filter((id): id is number => id !== null) || [];

  // Transform steps if present
  const transformedSteps = recipeData.steps
    ? recipeData.steps.map((step) => transformStepFromBackend(step))
    : undefined;

  // Transform preparations if present
  const transformedPreparations =
    recipeData.preparations && Array.isArray(recipeData.preparations)
      ? recipeData.preparations.map((prep: any) => {
          const clv =
            prep.custom_list_values &&
            typeof prep.custom_list_values === 'object'
              ? prep.custom_list_values
              : {};
          return {
            id: prep.id,
            name: prep.name || '',
            dosage_qty: prep.dosage_qty,
            // Prefer master FK; fall back to custom free-text dosage values
            unit_id: prep.unit_id ?? clv.unit ?? undefined,
            frequency_id: prep.frequency_id ?? clv.frequency ?? undefined,
            timing_id: prep.timing_id ?? clv.timing ?? undefined,
            meal_timing_id: prep.meal_timing_id ?? clv.meal_timing ?? undefined,
            duration_days: prep.duration_days,
            duration_unit_id:
              prep.duration_unit_id ?? clv.duration_unit ?? undefined,
            max_limit_days: prep.max_limit_days,
            max_duration_unit_id:
              prep.max_duration_unit_id ?? clv.max_duration_unit ?? undefined,
            is_indefinite: !!prep.is_indefinite,
            apply_topically: !!prep.apply_topically,
            apply_instruction: prep.apply_instruction,
            topical_alternatives: prep.topical_alternatives,
            application_steps: prep.application_steps
              ? parseJsonArray(prep.application_steps).join('\n')
              : '',
            dosage_instructions: prep.dosage_instructions,
            how_many_days: prep.how_many_days,
            how_many_days_unit_id:
              prep.how_many_days_unit_id ?? clv.how_many_days_unit ?? undefined,
            gap_days: prep.gap_days,
            gap_days_unit_id:
              prep.gap_days_unit_id ?? clv.gap_days_unit ?? undefined,
            apply_for_value: prep.apply_for_value,
            apply_for_uom: prep.apply_for_uom,
            preparation_time: prep.preparation_time || prep.prep_time_minutes,
            prep_time_minutes: prep.prep_time_minutes || prep.preparation_time,
            preparation_time_unit_id: prep.preparation_time_unit_id,
            servings: prep.servings,
            custom_list_values: Object.keys(clv).length ? clv : undefined,
            preparation_video: toAbsoluteMediaUrl(
              prep.preparation_video || prep.preparation_video_url,
            ),
            preparation_video_url: toAbsoluteMediaUrl(
              prep.preparation_video || prep.preparation_video_url,
            ),
            ingredients: prep.ingredients
              ? prep.ingredients.map((ing: any) => {
                  const recipeIng =
                    ing.RecipeIngredient ||
                    ing.recipe_ingredient ||
                    ing.recipeIngredient ||
                    {};
                  return transformIngredientFromBackend({
                    ...ing,
                    ...recipeIng,
                  });
                })
              : [],
            preparation_steps: (() => {
              const stepsList = prep.steps || prep.preparation_steps;
              return Array.isArray(stepsList)
                ? stepsList.map((step: any) => transformStepFromBackend(step))
                : [];
            })(),
          };
        })
      : [];

  return {
    id: recipeData.id,
    author_id: recipeData.author_id,
    title: recipeData.title || '',
    description: recipeData.description,
    category_id: recipeData.category_id,
    category:
      recipeData.category &&
      typeof recipeData.category === 'object' &&
      recipeData.category !== null &&
      Object.prototype.hasOwnProperty.call(recipeData.category, 'name')
        ? {
            id: (recipeData.category as { id?: string | number }).id,
            name: (recipeData.category as { name?: string }).name,
          }
        : undefined, // Include category object if present
    categories: categories, // Include categories array
    preparation_time: recipeData.prep_time_minutes, // Map prep_time_minutes → preparation_time
    servings: recipeData.servings,
    warnings: recipeData.warnings,
    side_effects: recipeData.side_effects,
    nutritional_info: recipeData.nutritional_info,
    cover_image_url: toAbsoluteMediaUrl(recipeData.cover_image), // Map cover_image → cover_image_url
    cover_gallery_images: (() => {
      const images =
        recipeData.cover_gallery_images ||
        (recipeData as { cover_gallery_image_urls?: unknown })
          .cover_gallery_image_urls;
      return parseJsonArray(images)
        .map((u: unknown) => toAbsoluteMediaUrl(String(u)))
        .filter((u): u is string => !!u);
    })(),
    cover_video_url: toAbsoluteMediaUrl(recipeData.cover_video), // Map cover_video → cover_video_url
    preparation_video_url: toAbsoluteMediaUrl(recipeData.preparation_video), // Map preparation_video → preparation_video_url
    status: recipeData.status,
    updatedAt: recipeData.updatedAt,
    doshas: doshaNames,
    dosha_ids: doshaIds,
    doshas_note: recipeData.doshas_note,
    doshas_ok_for_others: recipeData.doshas_ok_for_others,
    seasons_note: recipeData.seasons_note,
    seasons_ok_for_others: recipeData.seasons_ok_for_others,
    gender_note: recipeData.gender_note,
    gender_ok_for_others: recipeData.gender_ok_for_others,
    age_group_note: recipeData.age_group_note,
    age_group_ok_for_others: recipeData.age_group_ok_for_others,
    region_note: recipeData.region_note,
    region_ok_for_others: recipeData.region_ok_for_others,
    maternity_note: recipeData.maternity_note,
    maternity_ok_for_others: recipeData.maternity_ok_for_others,
    ingredients: transformedIngredients.map((ing) => ({
      id: ing.id,
      name: ing.name || '',
      quantity: String(ing.quantity),
      unit: ing.unit,
      contraindications: ing.contraindications,
      qualification: ing.qualification,
      is_substitute: ing.is_substitute,
      skip_for_contraindication: ing.skip_for_contraindication,
      parent_recipe_ingredient_id: ing.parent_recipe_ingredient_id,
      parent_contraindication_id: ing.parent_contraindication_id,
      type: ing.type,
      apply_topically: ing.apply_topically,
      how_often: ing.how_often,
      timing: ing.timing,
      meal_timing: ing.meal_timing,
      duration_value: ing.duration_value,
      duration_unit: ing.duration_unit,
      can_use_indefinitely: ing.can_use_indefinitely,
      stop_after: ing.stop_after,
      stop_after_unit: ing.stop_after_unit,
      size_unit: ing.size_unit,
      dosage_instructions: (ing as any).dosage_instructions,
    })),
    seasons: seasonNames,
    season_ids: seasonIds,
    tags: tagNames,
    tag_ids: tagIds,
    steps: transformedSteps, // Include steps
    preparations: transformedPreparations, // Include preparations

    approvals: recipeData.approvals, // Include approvals
    adminNotes: recipeData.adminNotes, // Include adminNotes
    // Medical / suitability data
    gender_ids: genderIds,
    age_group_ids: ageGroupIds,
    region_ids: regionIds,
    helpsWithDiseases: symptomIds,
    contraindications: contraIds,
    suitability_json: parseJsonArray(recipeData.suitability_json),
    q_and_a_json: parseJsonArray(recipeData.q_and_a_json),
    common_mistakes_json: parseJsonArray(recipeData.common_mistakes_json),
    ref_links: parseJsonArray(recipeData.ref_links),
    helpsWithDiseasesOther: recipeData.helps_with_diseases_other,
    // Dosage — prefer root (after API hoist); fall back to first preparation
    unit_id: (() => {
      const raw =
        recipeData.unit_id ?? transformedPreparations[0]?.unit_id ?? null;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    frequency_id: (() => {
      const raw =
        recipeData.frequency_id ??
        transformedPreparations[0]?.frequency_id ??
        null;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    timing_id: (() => {
      const raw =
        recipeData.timing_id ?? transformedPreparations[0]?.timing_id ?? null;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    meal_timing_id: (() => {
      const raw =
        recipeData.meal_timing_id ??
        transformedPreparations[0]?.meal_timing_id ??
        null;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    duration_days: (() => {
      const raw =
        recipeData.duration_days ??
        transformedPreparations[0]?.duration_days ??
        null;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    duration_unit_id: (() => {
      const raw =
        recipeData.duration_unit_id ??
        transformedPreparations[0]?.duration_unit_id ??
        null;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    max_limit_days: (() => {
      const raw =
        recipeData.max_limit_days ??
        transformedPreparations[0]?.max_limit_days ??
        null;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    max_duration_unit_id: (() => {
      const raw =
        recipeData.max_duration_unit_id ??
        transformedPreparations[0]?.max_duration_unit_id ??
        null;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    dosage_qty: recipeData.dosage_qty ?? transformedPreparations[0]?.dosage_qty,
    dosage_instructions:
      recipeData.dosage_instructions ??
      transformedPreparations[0]?.dosage_instructions,
    apply_for_value: (() => {
      const raw =
        (recipeData as { apply_for_value?: unknown }).apply_for_value ??
        transformedPreparations[0]?.apply_for_value;
      if (raw === undefined || raw === null || raw === '') return undefined;
      const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
      return Number.isFinite(n) && n > 0 ? n : undefined;
    })(),
    apply_for_uom: (() => {
      const raw =
        (recipeData as { apply_for_uom?: unknown }).apply_for_uom ??
        transformedPreparations[0]?.apply_for_uom;
      if (raw === undefined || raw === null || String(raw).trim() === '')
        return undefined;
      const text = String(raw).trim();
      const u = text.toUpperCase();
      if (u === 'MINUTES' || u === 'MINUTE' || u === 'MINS' || u === 'MIN')
        return 'MINUTES';
      if (u === 'HOURS' || u === 'HOUR' || u === 'HRS' || u === 'HR')
        return 'HOURS';
      return text;
    })(),
    how_many_days: (() => {
      const raw =
        (recipeData as { how_many_days?: number | string }).how_many_days ??
        transformedPreparations[0]?.how_many_days;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    how_many_days_unit_id: (() => {
      const raw =
        (recipeData as { how_many_days_unit_id?: number | string })
          .how_many_days_unit_id ??
        transformedPreparations[0]?.how_many_days_unit_id;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    gap_days: (() => {
      const raw =
        (recipeData as { gap_days?: number | string }).gap_days ??
        transformedPreparations[0]?.gap_days;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    gap_days_unit_id: (() => {
      const raw =
        (recipeData as { gap_days_unit_id?: number | string })
          .gap_days_unit_id ?? transformedPreparations[0]?.gap_days_unit_id;
      return raw !== undefined && raw !== null ? Number(raw) : undefined;
    })(),
    is_indefinite: (() => {
      const root = (recipeData as { is_indefinite?: boolean }).is_indefinite;
      if (root !== undefined && root !== null) return !!root;
      return !!transformedPreparations[0]?.is_indefinite;
    })(),
    apply_topically: (() => {
      const root = (recipeData as { apply_topically?: boolean })
        .apply_topically;
      if (root !== undefined && root !== null) return !!root;
      return !!transformedPreparations[0]?.apply_topically;
    })(),
    contraindications_notes:
      (recipeData as { contraindications_notes?: string | null })
        .contraindications_notes ?? undefined,
    medical_conditions_grid: (() => {
      const fromGrid = parseJsonArray(
        recipeData.medical_conditions_grid,
      ) as Array<{
        condition_id: number | string;
        name?: string | null;
        status: string;
        possible_reason?: string | null;
      }>;
      if (fromGrid.length > 0) return fromGrid;
      const fromAssoc = Array.isArray(recipeData.medical_conditions)
        ? recipeData.medical_conditions.map((mc: any) => ({
            condition_id: mc.id ?? mc.condition_id ?? mc.medical_condition_id,
            name: mc.name ?? null,
            status: mc.RecipeMedicalCondition?.status || mc.status || 'ok',
            possible_reason:
              mc.RecipeMedicalCondition?.possible_reason ??
              mc.possible_reason ??
              null,
          }))
        : [];
      return fromAssoc;
    })(),
    // Use extracted IDs from natural_conditions association, fallback to direct natural_condition_ids
    natural_condition_ids:
      naturalConditionIds.length > 0
        ? naturalConditionIds
        : (recipeData.natural_condition_ids as Array<string | number>) || [],
    recommended_diet: recipeData.recommended_diet,
    diet_to_avoid: recipeData.diet_to_avoid,
    general_exercise_notes: (recipeData as { general_exercise_notes?: string })
      .general_exercise_notes,
    // Preserve recipe-level custom free-text entries (incl. custom exercises)
    custom_list_values:
      (recipeData as { custom_list_values?: Record<string, string[]> })
        .custom_list_values ?? undefined,
    symptoms_grid: symptomsGrid,
    contraindications_grid: contraindicationsGrid,
    // Exercises - map backend format to wizard format (exercise_id, exercise_type_id, note, name)
    exercises:
      (
        recipeData as {
          exercises?: Array<{
            id?: string | number;
            name?: string;
            english_name?: string;
            parent_id?: string | number | null;
            RecipeExercise?: { note?: string };
            recipe_exercise?: { note?: string };
          }>;
        }
      ).exercises?.map((ex) => {
        const re =
          ex.RecipeExercise ||
          (ex as { recipe_exercise?: { note?: string } }).recipe_exercise;
        return {
          exercise_id: ex.id,
          exercise_type_id:
            (ex as { parent_id?: string | number | null }).parent_id ??
            undefined,
          note: re?.note || '',
          name: ex.name || ex.english_name || '',
        };
      }) || [],
    edit_version: recipeData.edit_version,
    // Team identity — must survive load→edit→save or Share with Team is wiped to personal.
    visibility_scope:
      (recipeData as { visibility_scope?: 'personal' | 'team' })
        .visibility_scope || 'personal',
    on_behalf_of_team_id: (
      recipeData as { on_behalf_of_team_id?: string | number | null }
    ).on_behalf_of_team_id,
    created_by_user_id: (
      recipeData as { created_by_user_id?: string | number | null }
    ).created_by_user_id,
  };
};
